import React from 'react';
import './ContractPreview.css';

interface ContractPreviewProps {
    provider: {
        gieName: string;
        address: string;
        representative: string;
        function: string;
    };
    client: {
        name: string;
        address: string;
        representative: string;
        function: string;
    };
    offer: any;
    meta: {
        city: string;
        date: string;
        startDate?: string;
        endDate?: string;
    };
}

const ContractPreview: React.FC<ContractPreviewProps> = ({ provider, client, offer, meta }) => {
    // Calculate pricing
    const priceHT = offer?.discountPrice || offer?.price || 0;
    const originalPrice = offer?.price || 1350;
    const discountPercent = originalPrice > 0 ? (1 - priceHT / originalPrice) : 0;
    const remise = discountPercent;

    const parcoursNames = offer?.parcours?.map((p: any) => p.title).join(', ') || 'Développeur Web et Web Mobile (DWWM)';
    const startDate = meta.startDate || '29/12/2025';
    const endDate = meta.endDate || '29/12/2026';

    return (
        <div className="contract-preview-container">
            <h1 className="contract-title">CONTRAT DE PRESTATION DE SERVICES</h1>

            <div className="section">
                <h2 className="section-title">Entre les soussignés :</h2>
                <div className="section-content">
                    <p><strong>{provider.gieName || 'Edu-JALM'}</strong></p>
                    <p>Siège social : {provider.address || '37 Rue du petit droue, 28230 DROUE-SUR-DROUETTE'}</p>
                    <p>Représenté par {provider.representative || 'Taupin Jérémy'}, en qualité de {provider.function || 'Membre fondateur'},</p>
                    <p>Ci-après dénommé <span className="highlight">« le Prestataire »</span>,</p>
                    <br />
                    <p><strong>{client.name || 'Live Campus - Air Object'}</strong></p>
                    <p>Siège social : {client.address || '10, rue du pont de coude, 77163 DAMMARTIN-SUR-TIGEAUX'}</p>
                    <p>Représenté par {client.representative || 'Olivier Morvan'}, en qualité de {client.function || 'Directeur'},</p>
                    <p>Ci-après dénommé <span className="highlight">« le Client »</span>,</p>
                </div>
            </div>

            <div className="section">
                <h2 className="section-title">1. OBJET DU CONTRAT</h2>
                <div className="section-content">
                    <p>Le présent contrat a pour objet la licence d'utilisation d'une plateforme LMS (Learning Management System) et la location de contenus pédagogiques e-learning relatifs au parcours <span className="highlight">« {parcoursNames} »</span>, ainsi que les prestations de formation citées ci-dessous pour une durée de douze (12) mois, soit du <span className="highlight">{startDate} au {endDate}</span>.</p>
                    <p>Le prestataire fournit un accès technique à la plateforme LMS ainsi que les contenus du parcours mentionné.</p>
                    <p>Les prestations incluent :</p>
                    <ul>
                        <li>Accès 24/7 au LMS (unités pédagogiques, évaluations, plateforme de gestion).</li>
                        <li>Assistance technique sous 72h ouvrés.</li>
                        <li>Accompagnement pédagogique (réponses aux questions, correction des évaluations, préparation à la certification).</li>
                        <li>Accès au parcours <span className="highlight">« {parcoursNames} »</span>.</li>
                        <li>Location du LMS : <span className="highlight">4 mois intensifs</span> (plus de 200h/mois) et <span className="highlight">8 mois de maintien de connexion</span>.</li>
                        <li><strong>Formation {parcoursNames} (DWWM)</strong> – Titre Professionnel de Niveau 5, composé de deux (2) blocs de compétences.</li>
                    </ul>
                </div>
            </div>

            <div className="section">
                <h2 className="section-title">2. DESCRIPTIF DES PRODUITS</h2>
                <div className="section-content">
                    <p>La plateforme LMS contient le parcours suivant :</p>
                    <ul>
                        <li><strong>Formation {parcoursNames} (DWWM)</strong> – Titre Professionnel de Niveau 5, composé de deux (2) blocs de compétences avec cent soixante-douze (172) Modules.</li>
                        <li><strong>Formation Culture Générale IT</strong> – Formation de culture générale apportant des bases nécessaire au parcours {parcoursNames}, composé d'un (1) bloc de compétence avec treize (13) Modules. <i>disponible mi-janvier 2026</i></li>
                    </ul>
                    <p>Les contenus sont disponibles en ligne pour les utilisateurs désignés par le Client, pendant une durée de douze (12) mois par bloc, à compter de l'ouverture de l'accès. Toute demande de prolongation supérieur à 3 mois au-delà de cette durée fera l'objet d'une nouvelle facturation et d'un nouvel accord entre les Parties.</p>
                    <p>Les contenus pédagogiques suivants restent la propriété exclusive du Prestataire :</p>
                    <ul>
                        <li>Supports de cours (PDF, PPT, vidéos, podcasts).</li>
                        <li>Évaluations, QCM, corrigés, dossiers de certification.</li>
                        <li>Outils interactifs, codes source, templates.</li>
                        <li>Retours personnalisés et annotations des formateurs.</li>
                    </ul>
                </div>
            </div>

            <div className="section">
                <h2 className="section-title">3. CONDITIONS D’UTILISATION DE LA PLATEFORME</h2>
                <div className="section-content">
                    <p>L'accès à la plateforme et aux contenus est autorisé uniquement pour les utilisateurs communiqués par le Client. L'accès est nominatif, personnel et non cessible. Toute violation entraînera la suppression immédiate des droits d'accès.</p>
                    <p>Chaque utilisateur dispose d'un accès aux contenus pendant une durée de douze (12) mois par bloc. À l'issue de cette période, l'accès sera automatiquement révoqué sans notification préalable. Toute prolongation fera l'objet d'une inscription au sein de la plateforme de gestion ( inférieur à 3 mois au cumul ) ou d'une nouvelle commande.</p>
                    <p>Le prestataire est seul habilité à ajouter ou supprimer des utilisateurs en dehors de la plateforme de gestion, ainsi qu'à modifier les contenus.</p>
                    <p>Le client est seul habilité à ajouter ou supprimer des utilisateurs depuis la plateforme de gestion, toute inscription entraine une facturation.</p>
                    <p>Les documents suivants, disponibles en ligne, viennent renforcer le présent contrat :</p>
                    <ul>
                        <li><a href="https://edu-jalm.fr/of-cgu" className="link" target="_blank" rel="noopener noreferrer">Conditions Générales d’Utilisation (CGU) - https://edu-jalm.fr/of-cgu</a></li>
                        <li><a href="https://edu-jalm.fr/rgpd" className="link" target="_blank" rel="noopener noreferrer">Politique de protection des données (RGPD) - https://edu-jalm.fr/rgpd</a></li>
                        <li><a href="https://edu-jalm.fr/learner-rules" className="link" target="_blank" rel="noopener noreferrer">Règlement à destination des apprenants - https://edu-jalm.fr/learner-rules</a></li>
                    </ul>
                </div>
            </div>

            <div className="section">
                <h2 className="section-title">4. RESTRICTIONS D’USAGE DES CONTENUS</h2>
                <div className="section-content">
                    <p>Les contenus sont strictement réservés à un usage pédagogique interne. Toute revente, mise à disposition à des tiers ou sous-licence, de façon gratuite ou payante, est formellement interdite. Toute violation entraînera la responsabilité pleine et entière du Client.</p>
                </div>
            </div>

            <div className="section">
                <h2 className="section-title">5. PROPRIÉTÉ INTELLECTUELLE</h2>
                <div className="section-content">
                    <p>Tous les contenus, marques, droits d'auteur, et éléments techniques de la plateforme LMS sont la propriété exclusive du prestataire.</p>
                    <p>En cas de violation, le Client s’expose à des dommages et intérêts fixés à <span className="highlight">5 fois le montant du contrat par apprenant concerné</span> et à des poursuites judiciaires pour contrefaçon.</p>
                </div>
            </div>

            <div className="section">
                <h2 className="section-title">6. CONFIDENTIALITÉ</h2>
                <div className="section-content">
                    <p>Le Client s'engage à traiter la plateforme LMS et ses contenus comme des informations confidentielles et à empêcher toute divulgation à des tiers.</p>
                </div>
            </div>

            <div className="section">
                <h2 className="section-title">7. GARANTIE, MAINTENANCE ET CONFORMITÉ RGPD</h2>
                <div className="section-content">
                    <p>Le prestataire assure la maintenance technique de la plateforme LMS pendant toute la durée du contrat, hors dysfonctionnements causés par le Client ou ses utilisateurs.</p>
                    <p>Les Parties s'engagent à respecter la règlementation applicable en matière de protection des données à caractère personnel, notamment le Règlement Général sur la Protection des Données (RGPD). Le Client est responsable des données qu'il collecte via la plateforme, et s'engage à informer ses utilisateurs des traitements effectués et de leurs droits. Le prestataire met en place les mesures techniques nécessaires pour garantir la sécurité des données stockées sur la plateforme.</p>
                    <p>La responsabilité du prestataire ne saurait être engagée en cas de dysfonctionnement temporaire de la plateforme, de perte de données non imputable à sa faute, ou d'échec d'un apprenant dans le cadre de sa formation. En toute hypothèse, sa responsabilité est limitée au montant des sommes effectivement versées au titre du trimestre concerné.</p>
                </div>
            </div>

            <div className="section">
                <h2 className="section-title">8. DURÉE ET RÉSILIATION</h2>
                <div className="section-content">
                    <p>Le présent contrat est conclu pour une durée ferme de douze (12) mois. Il ne pourra être résilié de manière anticipée, sauf en cas de faute grave ou d'accord écrit entre les Parties.</p>
                    <h3>8.1. Tarification</h3>
                    <p>
                        <strong>Contenus Stater AiO DWWM</strong> : mille trois cent cinquante euros (1350)HT par apprenant inscrit moins remise de onze virgule onze (11,11)%, soit mille deux cent euros (1200) HT par apprenant inscrit. Facturation à chaque inscription d'un nouvel utilisateur, avec paiement d'un acompte de 30% à l’inscription (paiement sous trente et un (31) jours) et montant restant lors du passage de titre par l'apprenant ou à l'échéance du 9ème mois (au premier des 2 termes échues).
                        <br />
                        <i>Le prestataire se réserve le droit de réviser les tarifs annuellement. Cette révision fera l'objet d'un avis écrit un (1) mois avant la date de reconduction du contrat.</i>
                    </p>
                    <h3>8.2. Retard de paiement</h3>
                    <p>Tout retard de paiement entraîne des frais de <span className="highlight">1,5% par mois</span> sur les sommes dues.</p>
                </div>
            </div>

            <div className="table-container">
                <h3>8.3. Modalités de remboursement en cas de résiliation d'un apprenant</h3>
                <table>
                    <thead>
                        <tr>
                            <th>Situation</th>
                            <th>Facturation</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>&lt; 50h et aucune activité</td>
                            <td>vingt-cinq (25%) de l'acompte (remboursement de 75% de l'acompte)</td>
                        </tr>
                        <tr>
                            <td>&lt; 50h avec activité</td>
                            <td>Frais à l'acte + vingt-cinq (25%) de l'acompte</td>
                        </tr>
                        <tr>
                            <td>&gt; 50h</td>
                            <td>Facturation au pro-rata + Frais à l'acte</td>
                        </tr>
                    </tbody>
                </table>
                <p> Les actes sont facturés selon le tableau suivant : </p>
                <table>
                    <thead>
                        <tr>
                            <th>Acte</th>
                            <th>Tarification</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Réponse sur le forum</td>
                            <td>Sept (7) €</td>
                        </tr>
                        <tr>
                            <td>Correction d'une evaluation de bloc</td>
                            <td>Trente (30) €</td>
                        </tr>
                        <tr>
                            <td>Correction de toute les évaluations de bloc</td>
                            <td>Facturation totale</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="section">
                <h2 className="section-title">9. PÉNALITÉE DE RETARD</h2>
                <div className="section-content">
                    <ul>
                        <li><span className="highlight">Frais de retard</span> : <span className="highlight">1,5% par mois</span> (soit <span className="highlight">18% par an</span>) sur les sommes dues, à compter du lendemain de l’échéance.</li>
                        <li><span className="highlight">1er impayé</span> : Suspension de l’accès pour l’apprenant concerné après 7 jours suivant la 2ème relance.</li>
                        <li><span className="highlight">2ème impayé effectif</span> : Période de tolérance de 15 jours supplémentaires si des apprenants sont en cours de certification, puis suspension globale.</li>
                    </ul>
                    <i>Les relances sont envoyées par email et effectuées 15 jours après l'échéance non résolue.</i>
                    <h3>9.4. Médiation en cas de difficultés de paiement</h3>
                    <p>En cas de difficultés avérées de paiement, l’OF peut saisir le Prestataire pour engager une médiation en vue d’établir un échéancier ou un accord de délais supplémentaires.</p>
                </div>
            </div>

            <div className="section">
                <h2 className="section-title">10. MODIFICATIONS DU CONTRAT</h2>
                <div className="section-content">
                    <p>Toute commande complémentaire ou modification substantielle du contrat fera l'objet d'un avenant signé par les deux Parties.</p>
                </div>
            </div>

            <div className="section">
                <h2 className="section-title">11. ARCHIVAGE ET PREUVES</h2>
                <div className="section-content">
                    <p>Les Parties reconnaissent la validité des connexions, journaux d'activité (logs) et historiques d'utilisation issus de la plateforme LMS en tant qu'éléments de preuve en cas de litige relatif à l'exécution du présent contrat.</p>
                </div>
            </div>

            <div className="signature-section">
                <div className="signature-block">
                    <p><strong>Pour le Prestataire,</strong></p>
                    <p>{provider.gieName || 'Edu-JALM'}</p>
                    <div className="signature-line"></div>
                    <p>{provider.representative || 'Taupin Jérémy'}</p>
                    <p>Fait à {meta.city || 'Droue-sur-Drouette'}, le {meta.date || '23/12/2025'}</p>
                </div>
                <div className="signature-block">
                    <p><strong>Pour le Client,</strong></p>
                    <p>{client.name || 'Live Campus - Air Object'}</p>
                    <div className="signature-line"></div>
                    <p>{client.representative || 'Olivier Morvan'}</p>
                    <p>Fait à {meta.city || 'Droue-sur-Drouette'}, le {meta.date || '23/12/2025'}</p>
                </div>
            </div>

            <div className="annexes">
                <p><strong>Annexes :</strong> Détail des contenus pédagogiques, politique RGPD (https://edu-jalm.fr/rgpd), Conditions Générales d'utilisation (https://edu-jalm.fr/of-cgu).</p>
            </div>
        </div>
    );
};

export default ContractPreview;
